import React, {CSSProperties, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {CoreDoms, CoreEvent, Viewers, ViewMode} from '@taopix/taopix-core';
import {ComputedPageItem} from '@taopix/taopix-core/dist/types/types';
import {Theme} from '@taopix/taopix-design-system';
import classNames from 'classnames';
import {DialogStatus} from '../../../../Enums';
import {UserFlowStep} from '../../../../Facade/UserFlow/BaseUserFlow';
import {UserFlowType} from '../../../../Facade/UserFlow/UserFlowFacade';
import useSettings from '../../../../Hooks/useSettings';
import useViewMode from '../../../../Hooks/useViewMode';
import ShuffleLayouts from '../../../../Modules/ShuffleLayouts';
import {LayoutSelector, PageCountSelector} from '../../../../Selectors';
import {useTaopix} from '../../../Context/TaopixContext';
import {useUserFlowState} from '../../../Context/UserFlow/UserFlowContext';
import {UserInterfaceActions} from '../../../Context/UserInterface/UserInterfaceActionTypes';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import {useExperience} from '../../../Experience/ExperienceProvider';
import LayoutLockSwitch from '../../../LayoutLockSwitch/LayoutLockSwitch';
import NextPageButton from '../../../Pagination/NextPageButton';
import Pagination from '../../../Pagination/Pagination';
import PreviousPageButton from '../../../Pagination/PreviousPageButton';
import PageViewFactory from '../../../Views/PageViewFactory';
import WorkareaToolbar from './WorkareaToolbar';
import {useProjectViewerKeyboardCommands} from '../../../../Hooks/useProjectViewerShortcuts';
import {useTranslation} from 'react-i18next';

export type ProjectProps = {
  selectedItem: ComputedPageItem;
};

const Project = ({selectedItem}: ProjectProps): JSX.Element => {
  const app = useTaopix();
  const settings = useSettings();
  const ui = useUiState();
  const userFlow = useUserFlowState();
  const {experience} = useExperience();
  const [viewMode] = useViewMode();
  const {t} = useTranslation();
  useProjectViewerKeyboardCommands();

  const [pageHighlightsActive, setPageHighlightsActive] = useState<boolean>(false);

  // get some props from the core
  const stateManager = app.core.get('stateManager');

  // get the layout from the store
  const layout = useSelector(LayoutSelector);

  // get the pageCount for pagination
  const pageCount = useSelector(PageCountSelector);
  const [snapStatus, setSnapStatus] = useState(true);

  // checks for showing save prompt returns true if no known dialogs are open
  // To Review - this will not catch all dialogs, not sure the way to do that,
  // all portal ids end in -portal check that none are in dom?
  const checkUiDialogs = () => {
    return (
      !ui.state.calendarCloseDialog &&
      !ui.state.calendarEditPictureDialog &&
      ui.state.dialogs.coverSpineChangeNotification === DialogStatus.Closed &&
      ui.state.dialogs.pageCountChangeNotification === DialogStatus.Closed &&
      ui.state.dialogs.projectLayoutChangeNotification === DialogStatus.Closed &&
      !ui.state.languageDialogOpen &&
      !userFlow.getCurrentStep(UserFlowType.Save).includes(UserFlowStep.ProjectName) &&
      !userFlow.isUserFlowActive(UserFlowType.Save) &&
      !userFlow.isUserFlowActive(UserFlowType.Share) &&
      !userFlow.isUserFlowActive(UserFlowType.Upgrade)
    );
  };

  const showSavePrompt =
    !ui.state.busy &&
    ui.state.saveCount < 1 &&
    checkUiDialogs() &&
    userFlow.getCurrentStep(UserFlowType.UserFlow) ===
      `${UserFlowType.UserFlow}.${UserFlowStep.None}`;

  // subscribe to global page events
  useEffect(() => {
    const tokens: string[] = [];

    tokens.push(
      app.core.subscribe(CoreEvent.UPDATE_ITEM, (msg: string, data: any) => {
        stateManager.dispatch(data);
      })
    );

    tokens.push(
      app.core.subscribe(CoreEvent.CORE_BUSY, (msg: string, data: any) => {
        ui.dispatch(UserInterfaceActions.setIsBusy(data));
      })
    );

    tokens.push(
      app.core.subscribe(CoreEvent.SNAP_STATUS_TOGGLE, (msg: string, data: any): void => {
        setSnapStatus(data);
      })
    );

    tokens.push(
      app.core.subscribe(CoreEvent.ZOOM_CHANGED, (msg: string, level: number) => {
        if (ui.state.zoomLevel !== level) {
          ui.dispatch(UserInterfaceActions.setZoomLevel(Number(level.toFixed(2))));
        }
      })
    );

    tokens.push(
      app.core.subscribe(CoreEvent.SET_SELECTED_PAGENUMBER, (msg: string, data: any) => {
        ui.dispatch(UserInterfaceActions.setPageNumber(data));
      })
    );

    tokens.push(
      app.core.subscribe(CoreEvent.SET_SELECTED_SIDE, (msg: string, data: any) => {
        ui.dispatch(UserInterfaceActions.setSelectedSide(data));
      })
    );

    tokens.push(
      app.core.subscribe(CoreEvent.SWAP_ACTIVE, (msg: string, data: any) => {
        ui.dispatch(UserInterfaceActions.setSwapActive(data));
      })
    );

    tokens.push(
      app.core.subscribe(CoreEvent.TEXT_STATES_CHANGE, (msg: string, data: any) => {
        if (Object.prototype.hasOwnProperty.call(data, 'isWaitingResponse')) {
          ui.dispatch(UserInterfaceActions.setTextIsProcessing(data.isWaitingResponse));
        }
      })
    );

    tokens.push(
      app.core.subscribe(CoreEvent.NON_ACCESSIBLE_ICON_MOUSEDOWN, (msg: string, data: any) => {
        ui.dispatch(
          UserInterfaceActions.setInsideCoverNotEditableTip({
            open: true,
            title: data.title,
            message: data.message,
          })
        );
      })
    );

    return () => {
      tokens.forEach((token, index) => {
        app.core.unsubscribe(token);
        delete tokens[index];
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem?.ref, ui.state.zoomLevel, layout]);

  useEffect(() => {
    const tokens: string[] = [];

    if (settings.state.authentication.userRegistered) {
      tokens.push(
        app.core.subscribe(CoreEvent.INACTIVITY_DETECTED, (msg: string, data: any) => {
          // User is registered
          // app is not busy
          // Inactivity dialog not already showing
          // TODO: Test if SSO
          // TODO: Test that we are not in the share preview window in MediaAlbumWeb
          if (
            app.state.loaded &&
            !ui.state.busy &&
            !userFlow.getCurrentStep(UserFlowType.Auth).includes(UserFlowStep.Logout)
          ) {
            userFlow.setCurrentStep(`${UserFlowStep.Auth}.${UserFlowStep.Logout}`);
          }
        })
      );
    }

    tokens.push(
      app.core.subscribe(CoreEvent.SAVE_KEYBOARD, () => {
        userFlow.activateUserFlow(UserFlowType.Save);
      })
    );

    return () => {
      tokens.forEach((token, index) => {
        app.core.unsubscribe(token);
        delete tokens[index];
      });
    };
  }, [settings.state.authentication.userRegistered]);

  useEffect(() => {
    const tokens: string[] = [];

    tokens.push(
      app.core.subscribe(CoreEvent.SAVE_PROMPT, (msg: string, data: any) => {
        if (showSavePrompt) {
          ui.dispatch(UserInterfaceActions.setSavePrompt(true));
        }
      })
    );

    return () => {
      tokens.forEach((token, index) => {
        app.core.unsubscribe(token);
        delete tokens[index];
      });
    };
  }, [showSavePrompt]);

  useEffect(() => {
    if (
      experience.EDITOR.workArea.listView.layout ||
      experience.EDITOR.workArea.singlePage.layout
    ) {
      app.core.get('stateManager').addModule('shuffleLayout', new ShuffleLayouts() as any);
    }
  }, [experience]);

  const containerRef = React.useRef<HTMLDivElement>(null);

  const workAreaClassNames = classNames(
    'flex',
    'flex-col',
    'flex-1',
    'relative',
    'min-h-0',
    'items-center',
    'overflow-hidden',
    'transition-[transform]',

    // Move the workarea up to position it in the focus mode area correctly:
    'sm:focusMode:translate-y-[var(--workareaOffset)]'
  );

  const paginationWrapperClasses = classNames(
    'relative',
    'flex',
    'w-full',
    'items-center',
    'flex-1',
    'overflow-hidden' // prevents this container getting bigger when the project viewer is zoomed
  );

  const projectViewerClassNames = classNames(
    'flex',
    'relative',
    'w-full',
    'h-full',
    'transition-[all] duration-[0.3s] ease-out',
    ui.state.zoomLevel === 100 && 'justify-center',
    'snap-mandatory',

    // List and Preview views
    [ViewMode.List, ViewMode.Preview].includes(viewMode) &&
      ui.state.zoomLevel === 100 &&
      snapStatus &&
      'snap-y',

    // Single page view
    ViewMode.Single === viewMode && [
      'flex-col',
      'transition-[padding]',
      '[.focus-mode-off_&]:pt-[60rem]',
      ui.state.zoomLevel === 100 && 'snap-x',
    ],

    pageHighlightsActive && 'pageHighlightsActive'
  );

  let insetWidthAttr = '0';
  let insetHeightAttr = '0';

  // Set the inset sizes for the different view modes and experiences
  // By default, the canvases will be sized to fill the available space inside the project-viewer
  // The below values reduce this available space to account for other elements that sit in or over that space,
  // e.g. the page name or page toolbar
  switch (viewMode) {
    case ViewMode.List:
      insetWidthAttr = ui.state.isSmallScreen ? '40' : '80';
      insetHeightAttr = ui.state.isSmallScreen ? '260' : '200';
      break;
    case ViewMode.Preview:
      insetWidthAttr = ui.state.isSmallScreen ? '40' : '300';
      insetHeightAttr = ui.state.isSmallScreen ? '200' : '300';
      break;
    case ViewMode.Single:
      insetWidthAttr = ui.state.isSmallScreen ? '40' : '140';
      insetHeightAttr = ui.state.isSmallScreen
        ? experience.EDITOR.workArea.singlePage.pageThumbnailBar
          ? '140'
          : '240'
        : experience.EDITOR.workArea.singlePage.pageThumbnailBar
        ? '120'
        : '160';
      break;
  }

  const tpxCoreAttrs = {
    [CoreDoms.ProjectViewerAttr]: Viewers.Primary,
    [CoreDoms.InsetWidthAttr]: insetWidthAttr,
    [CoreDoms.InsetHeightAttr]: insetHeightAttr,
    [CoreDoms.ResizeThresholdAttr]: '20',
    [CoreDoms.ListDirectionAttr]: viewMode === ViewMode.Single ? 'row' : 'column',
  };

  useEffect(() => {
    setPageHighlightsActive(ui.state.viewMode !== ViewMode.Preview);
  }, [viewMode, ui.state.isSmallScreen]);

  let workareaOffset = '0';
  let toolbarOffset = '0';

  switch (viewMode) {
    case ViewMode.List:
      workareaOffset = 'calc(-50% + (var(--focusAreaHeight) / 2 - var(--headerHeight)))'; // Positions the work area with its middle aligned with the middle of the focus area
      toolbarOffset = '98rem'; // Not sure why this value works; it's impossible to offset the toolbar relative how far the workarea has moved, as it was moved based on 50% of its height.
      break;
    case ViewMode.Single:
      workareaOffset = '-65rem';
      toolbarOffset = '14rem'; // Brings the viewerToolbar back down into view
      break;
    case ViewMode.Grid:
      workareaOffset = 'calc(0rem - var(--headerHeight))';
      break;
  }

  const workareaVars = {
    '--headerHeight': experience.EDITOR.header.headerHeight === 'sm' ? '51rem' : '61rem',
    '--paletteHeight':
      experience.EDITOR.toolPalette.mobileSlideUpPanels === 'msuc' ? '310rem' : '320rem',
    '--focusAreaHeight': 'calc(100svh - var(--paletteHeight))',
    '--workareaOffset': workareaOffset,
    '--toolbarOffset': toolbarOffset,
  } as CSSProperties;

  useEffect(() => {
    // Whenever the zoom level is 100, reset the snap status to true
    if (ui.state.zoomLevel === 100) setSnapStatus(true);
  }, [ui.state.zoomLevel]);

  return (
    <Theme
      element="main"
      aria-label={t('*:str_TitleProjectPreview', {defaultValue: 'Project preview'})}
      name={'tpx-theme-workarea'}
      tipLabel={'Workarea'}
      isRootTheme
      allowBorder={false}
      id="workarea"
      data-track-position
      className={workAreaClassNames}
      style={workareaVars}
      allowCorners={false}
    >
      <WorkareaToolbar layout={layout} />

      <div id={'paginationWrapper'} className={paginationWrapperClasses}>
        <div
          {...tpxCoreAttrs}
          className={projectViewerClassNames}
          ref={containerRef}
          id={'projectViewer'}
        >
          <PageViewFactory
            key={`page-view-factory-${layout.code}`}
            layout={layout}
            containerRef={containerRef}
          />
        </div>
        {experience.EDITOR.workArea.singlePage.nextPreviousPageButtons &&
          viewMode === ViewMode.Single &&
          !ui.state.isSmallScreen &&
          ui.state.zoomLevel === 100 && (
            <>
              <PreviousPageButton />
              <NextPageButton />
            </>
          )}
        {((experience.EDITOR.pageEditing.allowUsersToToggleLocking && viewMode === ViewMode.List) ||
          viewMode === ViewMode.Single) &&
          !ui.state.isSmallScreen && (
            // Show the large screen LayoutLockSwitch
            <div className={classNames('absolute', 'bottom-sm', 'right-sm', 'mt-sm', 'z-10')}>
              <LayoutLockSwitch />
            </div>
          )}
      </div>

      {ViewMode.Single === viewMode &&
        pageCount > 1 &&
        experience.EDITOR.workArea.singlePage.pageThumbnailBar && (
          <Pagination
            layout={layout}
            onChangePage={(pageNumber: number) =>
              app.core.get('abilities').setPage(pageNumber).then()
            }
          />
        )}
    </Theme>
  );
};

export default Project;
